// Effets cosmétiques (cosmétiques refaits) : auras, traînées, effets de tir, d'élimination et de mort.
// Particules en lot (ParticleContainer, une seule texture partagée) : des centaines d'étincelles pour le prix
// d'un sprite. Tout passe par `budget` (réglage « particules », animations réduites) et un plafond global ;
// les effets utiles au jeu (impacts, explosions annoncées) restent dans particles.ts et ne sont jamais rognés ici.
import { Container, Graphics, Particle, ParticleContainer, Rectangle, Texture, type Renderer } from 'pixi.js';

type Tex = 'dot' | 'spark' | 'flake' | 'smoke' | 'star' | 'bubble' | 'shard' | 'ring' | 'rune';
const TEXS: Tex[] = ['dot', 'spark', 'flake', 'smoke', 'star', 'bubble', 'shard', 'ring', 'rune'];
const CELL = 48;

/** Atlas des textures de particules, dessinées une fois en blanc (teintées à l'emploi). */
function buildAtlas(renderer: Renderer): Record<Tex, Texture> {
  const g = new Graphics();
  TEXS.forEach((t, i) => {
    const cx = i * CELL + CELL / 2;
    const cy = CELL / 2;
    switch (t) {
      case 'dot':
        for (let k = 8; k >= 1; k--) g.circle(cx, cy, k * 2.6).fill({ color: 0xffffff, alpha: 0.11 + (8 - k) * 0.02 });
        g.circle(cx, cy, 3).fill(0xffffff);
        break;
      case 'spark':
        g.ellipse(cx, cy, 20, 2.2).fill({ color: 0xffffff, alpha: 0.5 });
        g.ellipse(cx, cy, 12, 1.2).fill(0xffffff);
        break;
      case 'flake':
        for (let k = 0; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2;
          g.moveTo(cx, cy).lineTo(cx + Math.cos(a) * 16, cy + Math.sin(a) * 16).stroke({ width: 2.2, color: 0xffffff });
          const mx = cx + Math.cos(a) * 9;
          const my = cy + Math.sin(a) * 9;
          for (const d of [-0.7, 0.7]) g.moveTo(mx, my).lineTo(mx + Math.cos(a + d) * 5, my + Math.sin(a + d) * 5).stroke({ width: 1.6, color: 0xffffff });
        }
        break;
      case 'smoke':
        for (let k = 10; k >= 1; k--) g.circle(cx, cy, k * 2.2).fill({ color: 0xffffff, alpha: 0.07 });
        break;
      case 'star': {
        const pts: number[] = [];
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          const r = k % 2 ? 4 : 18;
          pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
        }
        g.poly(pts).fill(0xffffff);
        g.circle(cx, cy, 8).fill({ color: 0xffffff, alpha: 0.35 });
        break;
      }
      case 'bubble':
        g.circle(cx, cy, 14).stroke({ width: 2, color: 0xffffff, alpha: 0.9 });
        g.circle(cx - 5, cy - 5, 3).fill({ color: 0xffffff, alpha: 0.8 });
        break;
      case 'shard':
        g.poly([cx - 10, cy - 7, cx + 12, cy - 2, cx - 4, cy + 9]).fill(0xffffff);
        break;
      case 'ring':
        g.circle(cx, cy, 20).stroke({ width: 3, color: 0xffffff });
        g.circle(cx, cy, 20).stroke({ width: 7, color: 0xffffff, alpha: 0.25 });
        break;
      case 'rune':
        g.moveTo(cx - 8, cy + 12).lineTo(cx, cy - 12).lineTo(cx + 8, cy + 12).moveTo(cx - 10, cy + 2).lineTo(cx + 10, cy + 2).stroke({ width: 2.6, color: 0xffffff });
        g.circle(cx, cy - 12, 2.5).fill(0xffffff);
        break;
    }
  });
  const rt = renderer.generateTexture({ target: g, frame: new Rectangle(0, 0, TEXS.length * CELL, CELL), resolution: 2 });
  g.destroy();
  const out = {} as Record<Tex, Texture>;
  TEXS.forEach((t, i) => (out[t] = new Texture({ source: rt.source, frame: new Rectangle(i * CELL, 0, CELL, CELL) })));
  return out;
}

interface Live {
  p: Particle;
  layer: ParticleContainer;
  vx: number;
  vy: number;
  life: number;
  max: number;
  spin: number;
  drag: number;
  grav: number;
  s0: number;
  s1: number;
  a0: number;
  /** Orbite autour d'un point suivi (auras) : angle, rayon, vitesse angulaire. */
  orbit?: { c: { x: number; y: number }; a: number; r: number; w: number };
  /** 1 = apparition en fondu (lucioles, flocons). */
  fadeIn?: boolean;
  /** Teinte qui passe de tint à tint2 pendant la vie (braises). */
  tint2?: number;
  tint: number;
}

interface Bolt {
  g: Graphics;
  life: number;
  max: number;
}

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const lerpColor = (a: number, b: number, t: number) => {
  const ch = (c: number, s: number) => (c >> s) & 255;
  const m = (s: number) => Math.round(ch(a, s) + (ch(b, s) - ch(a, s)) * t) << s;
  return m(16) | m(8) | m(0);
};
function hsl(h: number, s: number, l: number): number {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  return (Math.round(f(0) * 255) << 16) | (Math.round(f(8) * 255) << 8) | Math.round(f(4) * 255);
}

/** Position et vitesse d'un tank pour ses effets continus. */
export interface FxTank {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Rayon affiché (unités du monde). */
  r: number;
  /** Direction de la caisse (rad), pour placer l'arrière de la traînée. */
  hull: number;
}

const MAX_LIVE = 1400;
/** Les tailles passées aux émetteurs sont en rayons de tank ; la texture occupe une partie de sa case. */
const SIZE_K = 1.4;

export class Fx {
  readonly root = new Container();
  /** Sous les tanks (traînées), au-dessus (auras, tirs, éliminations). */
  readonly below = new Container();
  private readonly tex: Record<Tex, Texture>;
  private readonly addB: ParticleContainer;
  private readonly addA: ParticleContainer;
  private readonly normB: ParticleContainer;
  private readonly normA: ParticleContainer;
  private readonly bolts = new Container();
  private live: Live[] = [];
  private boltList: Bolt[] = [];
  private acc = new Map<string, number>();
  /** Dernier point arrière de chaque traînée : les particules sont réparties le long du trajet (ruban continu). */
  private lastRear = new Map<string, { x: number; y: number }>();
  private runes = new Map<string, Live[]>();
  private time = 0;
  /** 0,3 à 1 selon le réglage « particules » ; 0 coupe les effets cosmétiques. */
  budget = 1;

  constructor(renderer: Renderer) {
    this.tex = buildAtlas(renderer);
    const mk = (blendMode: 'add' | 'normal') =>
      new ParticleContainer({ dynamicProperties: { position: true, rotation: true, vertex: true, color: true }, blendMode });
    this.normB = mk('normal');
    this.addB = mk('add');
    this.normA = mk('normal');
    this.addA = mk('add');
    this.below.addChild(this.normB, this.addB);
    this.bolts.blendMode = 'add';
    this.root.addChild(this.normA, this.addA, this.bolts);
  }

  private spawn(o: {
    tex: Tex;
    x: number;
    y: number;
    vx?: number;
    vy?: number;
    life: number;
    size: number;
    size1?: number;
    tint: number;
    tint2?: number;
    alpha?: number;
    spin?: number;
    rot?: number;
    drag?: number;
    grav?: number;
    add?: boolean;
    below?: boolean;
    fadeIn?: boolean;
    orbit?: Live['orbit'];
  }): Live | null {
    if (this.live.length >= MAX_LIVE) return null;
    const layer = o.below ? (o.add ? this.addB : this.normB) : o.add ? this.addA : this.normA;
    const s0 = (o.size * SIZE_K) / CELL;
    const p = new Particle({ texture: this.tex[o.tex], x: o.x, y: o.y, anchorX: 0.5, anchorY: 0.5, scaleX: s0, scaleY: s0, rotation: o.rot ?? 0, tint: o.tint, alpha: o.alpha ?? 1 });
    layer.addParticle(p);
    const l: Live = { p, layer, vx: o.vx ?? 0, vy: o.vy ?? 0, life: o.life, max: o.life, spin: o.spin ?? 0, drag: o.drag ?? 1, grav: o.grav ?? 0, s0, s1: ((o.size1 ?? o.size) * SIZE_K) / CELL, a0: o.alpha ?? 1, orbit: o.orbit, fadeIn: o.fadeIn, tint: o.tint, tint2: o.tint2 };
    this.live.push(l);
    return l;
  }

  /** Nombre d'émissions dues pour une clé à un débit donné (par seconde), budget compris. */
  private due(key: string, rate: number, dt: number): number {
    const v = (this.acc.get(key) ?? 0) + rate * this.budget * dt;
    const n = Math.floor(v);
    this.acc.set(key, v - n);
    return n;
  }

  private bolt(x0: number, y0: number, x1: number, y1: number, color: number, width = 2, life = 0.14): void {
    if (this.budget <= 0) return;
    const g = new Graphics();
    const pts = [x0, y0];
    const n = 7;
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const nx = -(y1 - y0);
      const ny = x1 - x0;
      const len = Math.hypot(nx, ny) || 1;
      const j = rnd(-0.12, 0.12) * Math.hypot(x1 - x0, y1 - y0);
      pts.push(x0 + (x1 - x0) * t + (nx / len) * j, y0 + (y1 - y0) * t + (ny / len) * j);
    }
    pts.push(x1, y1);
    g.poly(pts, false).stroke({ width: width * 3, color, alpha: 0.3 }).stroke({ width, color: 0xffffff });
    this.bolts.addChild(g);
    this.boltList.push({ g, life, max: life });
  }

  // ─── Effets continus ──────────────────────────────────────────────────

  /** Aura autour d'un tank ; `key` identifie le tank (accumulateurs d'émission). */
  aura(key: string, id: string | null, t: FxTank, dt: number): void {
    if (!id || this.budget <= 0) {
      this.clearRunes(key);
      return;
    }
    const r = t.r;
    const k = `a:${key}`;
    switch (id) {
      case 'aura:etincelles':
        for (let i = this.due(k, 20, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          const rr = r * rnd(0.95, 1.15);
          this.spawn({ tex: 'spark', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: Math.cos(a) * 30 - Math.sin(a) * 40, vy: Math.sin(a) * 30 + Math.cos(a) * 40, rot: a + Math.PI / 2, life: rnd(0.25, 0.5), size: r * 0.5, size1: r * 0.2, tint: Math.random() < 0.5 ? 0xffd36b : 0xfff4d6, add: true, drag: 0.9 });
        }
        break;
      case 'aura:flocons':
        for (let i = this.due(k, 7, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          const rr = r * rnd(0.9, 1.3);
          this.spawn({ tex: 'flake', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.6 + rnd(-8, 8), vy: t.vy * 0.6 + rnd(6, 16), life: rnd(1.2, 2), size: r * rnd(0.22, 0.38), tint: Math.random() < 0.4 ? 0xbfe6ff : 0xffffff, spin: rnd(-1.5, 1.5), fadeIn: true, alpha: 0.95 });
        }
        break;
      case 'aura:braises':
        for (let i = this.due(k, 16, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          const rr = r * rnd(0.7, 1.15);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * rr, y: t.y + Math.sin(a) * rr, vx: t.vx * 0.7 + rnd(-10, 10), vy: t.vy * 0.7 - rnd(25, 55), life: rnd(0.6, 1.1), size: r * rnd(0.25, 0.45), size1: r * 0.08, tint: 0xffe08a, tint2: 0xff3a12, add: true });
        }
        break;
      case 'aura:lucioles':
        for (let i = this.due(k, 3.5, dt); i > 0; i--) {
          this.spawn({ tex: 'dot', x: t.x, y: t.y, life: rnd(1.8, 2.8), size: r * rnd(0.3, 0.45), tint: Math.random() < 0.5 ? 0xd9ff6b : 0x9dffb0, add: true, fadeIn: true, orbit: { c: t, a: rnd(0, Math.PI * 2), r: r * rnd(1.05, 1.4), w: rnd(-1.4, 1.4) } });
        }
        break;
      case 'aura:orage':
        // Jaune électrique, loin du tank, 2,5 éclairs par seconde : jamais confondu avec le bouclier (bleu).
        if (this.due(`${k}:b`, 2.5, dt) > 0) {
          const a = rnd(0, Math.PI * 2);
          const a2 = a + rnd(0.6, 1.6);
          this.bolt(t.x + Math.cos(a) * r * 1.5, t.y + Math.sin(a) * r * 1.5, t.x + Math.cos(a2) * r * 1.55, t.y + Math.sin(a2) * r * 1.55, 0xffe45c, 1.4);
        }
        for (let i = this.due(k, 8, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * r * 1.5, y: t.y + Math.sin(a) * r * 1.5, life: 0.3, size: r * 0.3, size1: 0, tint: 0xfff3a8, add: true });
        }
        break;
      case 'aura:runes': {
        // Anneau permanent de huit runes qui tournent lentement autour du tank.
        let ring = this.runes.get(key);
        if (!ring) {
          ring = [];
          for (let i = 0; i < 8; i++) {
            const l = this.spawn({ tex: 'rune', x: t.x, y: t.y, life: 1e9, size: r * 0.42, tint: 0xc88cff, add: true, orbit: { c: t, a: (i / 8) * Math.PI * 2, r: r * 1.3, w: 0.6 } });
            if (l) ring.push(l);
          }
          this.runes.set(key, ring);
        }
        for (const l of ring) l.p.alpha = 0.55 + 0.45 * Math.sin(this.time * 3 + l.orbit!.a * 2);
        for (let i = this.due(k, 6, dt); i > 0; i--) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'dot', x: t.x + Math.cos(a) * r * 1.3, y: t.y + Math.sin(a) * r * 1.3, vy: -15, life: 0.8, size: r * 0.25, size1: 0, tint: 0xe2c2ff, add: true });
        }
        break;
      }
    }
    if (id !== 'aura:runes') this.clearRunes(key);
  }

  /** Tank disparu : ses accumulateurs, sa traînée et son anneau de runes sont oubliés. */
  forget(key: string): void {
    this.clearRunes(key);
    this.acc.delete(`a:${key}`);
    this.acc.delete(`a:${key}:b`);
    this.acc.delete(`t:${key}`);
    this.lastRear.delete(`t:${key}`);
  }

  private clearRunes(key: string): void {
    const ring = this.runes.get(key);
    if (!ring) return;
    for (const l of ring) l.life = 0;
    this.runes.delete(key);
  }

  /** Traînée derrière un tank qui roule. */
  trail(key: string, id: string | null, t: FxTank, dt: number): void {
    if (!id || this.budget <= 0) return;
    const speed = Math.hypot(t.vx, t.vy);
    const k = `t:${key}`;
    if (speed < 20) {
      this.lastRear.delete(k);
      return;
    }
    const dir = Math.atan2(t.vy, t.vx);
    const bx = t.x - Math.cos(dir) * t.r * 0.85;
    const by = t.y - Math.sin(dir) * t.r * 0.85;
    const prev = this.lastRear.get(k) ?? { x: bx, y: by };
    this.lastRear.set(k, { x: bx, y: by });
    const side = () => rnd(-0.45, 0.45) * t.r;
    const px = -Math.sin(dir);
    const py = Math.cos(dir);
    // Point au hasard sur le segment parcouru depuis l'image précédente, décalé sur le côté de s.
    const at = (s: number) => {
      const u = Math.random();
      return { x: prev.x + (bx - prev.x) * u + px * s, y: prev.y + (by - prev.y) * u + py * s };
    };
    switch (id) {
      case 'trail:poussiere':
        for (let i = this.due(k, speed * 0.08, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'smoke', x: p.x, y: p.y, vx: -Math.cos(dir) * 15, vy: -Math.sin(dir) * 15, life: rnd(0.5, 0.8), size: t.r * 0.5, size1: t.r * 1.0, tint: 0xa58f73, alpha: 0.4, below: true });
        }
        break;
      case 'trail:lumiere':
        for (let i = this.due(k, 45, dt); i > 0; i--) {
          const p = at(rnd(-0.1, 0.1) * t.r);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.55, size: t.r * 0.7, size1: t.r * 0.15, tint: 0x5fe3ff, add: true, below: true });
        }
        break;
      case 'trail:bulles':
        for (let i = this.due(k, 10, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'bubble', x: p.x, y: p.y, vx: rnd(-6, 6), vy: rnd(-14, -4), life: rnd(0.8, 1.3), size: t.r * rnd(0.18, 0.35), size1: t.r * 0.45, tint: 0xbdf1ff, alpha: 0.85, below: true });
        }
        break;
      case 'trail:flammes':
        for (let i = this.due(k, 50, dt); i > 0; i--) {
          const p = at(side() * 0.7);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, vx: -Math.cos(dir) * 60 + rnd(-15, 15), vy: -Math.sin(dir) * 60 + rnd(-15, 15), life: rnd(0.2, 0.4), size: t.r * rnd(0.45, 0.7), size1: t.r * 0.1, tint: 0xfff0a0, tint2: 0xff3a10, add: true, below: true });
        }
        break;
      case 'trail:etoiles':
        for (let i = this.due(k, 14, dt); i > 0; i--) {
          const p = at(side());
          this.spawn({ tex: 'star', x: p.x, y: p.y, life: rnd(0.6, 1.1), size: t.r * rnd(0.25, 0.45), size1: 0, tint: [0xffffff, 0xffe07a, 0xa9c8ff][Math.floor(Math.random() * 3)], spin: rnd(-3, 3), add: true, below: true });
        }
        break;
      case 'trail:arcenciel':
        for (let i = this.due(k, 55, dt); i > 0; i--) {
          const p = at(rnd(-0.12, 0.12) * t.r);
          this.spawn({ tex: 'dot', x: p.x, y: p.y, life: 0.8, size: t.r * 0.75, size1: t.r * 0.2, tint: hsl((this.time * 140 + i * 9) % 360, 0.95, 0.6), add: true, below: true });
        }
        break;
    }
  }

  // ─── Effets ponctuels ─────────────────────────────────────────────────

  /** Effet de tir à la bouche d'un tube (la balle elle-même ne change pas). */
  shot(id: string | null, x: number, y: number, angle: number, size: number): void {
    if (!id || this.budget <= 0) return;
    const n = (c: number) => Math.max(1, Math.round(c * this.budget));
    switch (id) {
      case 'shot:etincelles':
        for (let i = 0; i < n(9); i++) {
          const a = angle + rnd(-0.5, 0.5);
          const v = rnd(120, 260);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: rnd(0.12, 0.25), size: size * 0.9, size1: size * 0.2, tint: 0xffd36b, add: true, drag: 0.88 });
        }
        break;
      case 'shot:fumee':
        for (let i = 0; i < n(5); i++) {
          const a = angle + rnd(-0.35, 0.35);
          const v = rnd(30, 80);
          this.spawn({ tex: 'smoke', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.5, 0.8), size: size * 0.6, size1: size * 1.8, tint: 0xc8ccd2, alpha: 0.4, drag: 0.94 });
        }
        break;
      case 'shot:plasma':
        this.spawn({ tex: 'ring', x, y, life: 0.22, size: size * 0.4, size1: size * 1.8, tint: 0x6fe8ff, add: true });
        this.spawn({ tex: 'dot', x, y, life: 0.15, size: size * 1.4, size1: size * 0.3, tint: 0xbff6ff, add: true });
        break;
      case 'shot:etoile':
        this.spawn({ tex: 'star', x, y, life: 0.2, size: size * 0.6, size1: size * 1.6, tint: 0xfff2b0, add: true, rot: angle });
        for (let i = 0; i < n(5); i++) {
          const a = angle + rnd(-0.8, 0.8);
          this.spawn({ tex: 'star', x, y, vx: Math.cos(a) * 140, vy: Math.sin(a) * 140, life: 0.35, size: size * 0.35, size1: 0, tint: 0xffe07a, add: true, drag: 0.9, spin: 6 });
        }
        break;
    }
  }

  /** Effet d'élimination, à la position de la victime. */
  kill(id: string | null, x: number, y: number, r: number): void {
    if (!id || this.budget <= 0) return;
    const n = (c: number) => Math.max(1, Math.round(c * this.budget));
    switch (id) {
      case 'kill:confettis':
        for (let i = 0; i < n(45); i++) {
          const a = rnd(0, Math.PI * 2);
          const v = rnd(60, 160);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 80, life: rnd(1, 1.6), size: r * rnd(0.25, 0.4), tint: hsl(rnd(0, 360), 0.9, 0.6), spin: rnd(-10, 10), drag: 0.95, grav: 260 });
        }
        break;
      case 'kill:onde':
        this.spawn({ tex: 'ring', x, y, life: 0.45, size: r, size1: r * 3, tint: 0x9fd8ff, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.6, size: r * 0.5, size1: r * 2.2, tint: 0xffffff, alpha: 0.6, add: true });
        this.spawn({ tex: 'dot', x, y, life: 0.25, size: r * 1.8, size1: r * 0.6, tint: 0xd8f0ff, add: true });
        break;
      case 'kill:foudre':
        for (let i = 0; i < 3; i++) this.bolt(x + rnd(-r, r), y - r * 4, x + rnd(-r * 0.3, r * 0.3), y, 0x8fd0ff, 2, 0.25);
        this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 2, size1: r * 0.6, tint: 0xbfe6ff, add: true });
        for (let i = 0; i < n(20); i++) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'spark', x, y, vx: Math.cos(a) * rnd(70, 160), vy: Math.sin(a) * rnd(70, 160), rot: a, life: 0.35, size: r * 0.7, size1: 0, tint: 0xbfe6ff, add: true, drag: 0.9 });
        }
        break;
      case 'kill:supernova':
        this.spawn({ tex: 'dot', x, y, life: 0.45, size: r * 2.6, size1: r * 0.8, tint: 0xfff2d0, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.7, size: r, size1: r * 4, tint: 0xff9a4a, add: true });
        this.spawn({ tex: 'ring', x, y, life: 0.55, size: r, size1: r * 3, tint: 0xff4f7b, add: true });
        for (let i = 0; i < n(60); i++) {
          const a = rnd(0, Math.PI * 2);
          const v = rnd(90, 220);
          this.spawn({ tex: i % 3 ? 'spark' : 'star', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, rot: a, life: rnd(0.5, 1), size: r * 0.8, size1: 0, tint: [0xfff2b0, 0xff9a4a, 0xff4f7b][i % 3], add: true, drag: 0.93 });
        }
        break;
    }
  }

  /** Effet de mort du tank, aux couleurs de ses pièces. */
  death(id: string | null, x: number, y: number, r: number, colors: number[]): void {
    if (!id || this.budget <= 0) return;
    const n = (c: number) => Math.max(1, Math.round(c * this.budget));
    const col = () => colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff;
    switch (id) {
      case 'death:eclats':
        for (let i = 0; i < n(28); i++) {
          const a = rnd(0, Math.PI * 2);
          const v = rnd(60, 180);
          this.spawn({ tex: 'shard', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: rnd(0.7, 1.2), size: r * rnd(0.3, 0.6), tint: col(), spin: rnd(-12, 12), drag: 0.9 });
        }
        this.spawn({ tex: 'dot', x, y, life: 0.3, size: r * 1.8, size1: r * 0.6, tint: 0xffc070, add: true });
        break;
      case 'death:fumee':
        for (let i = 0; i < n(14); i++) {
          const a = rnd(0, Math.PI * 2);
          this.spawn({ tex: 'smoke', x: x + Math.cos(a) * r * 0.5, y: y + Math.sin(a) * r * 0.5, vx: Math.cos(a) * 18, vy: Math.sin(a) * 18 - 14, life: rnd(1, 1.6), size: r * 0.9, size1: r * 2, tint: 0x2b2e33, alpha: 0.45, drag: 0.97 });
        }
        break;
      case 'death:implosion':
        for (let i = 0; i < n(36); i++) {
          const a = rnd(0, Math.PI * 2);
          const d = r * rnd(1.6, 2.2);
          this.spawn({ tex: 'dot', x: x + Math.cos(a) * d, y: y + Math.sin(a) * d, vx: -Math.cos(a) * d * 2.2, vy: -Math.sin(a) * d * 2.2, life: 0.45, size: r * 0.4, size1: r * 0.1, tint: col(), add: true });
        }
        this.spawn({ tex: 'ring', x, y, life: 0.45, size: r * 3, size1: r * 0.3, tint: 0xc88cff, alpha: 0.8, add: true });
        break;
      case 'death:vaporisation':
        for (let i = 0; i < n(40); i++) {
          this.spawn({ tex: 'dot', x: x + rnd(-r, r), y: y + rnd(-r, r), vx: rnd(-20, 20), vy: rnd(-90, -30), life: rnd(0.8, 1.5), size: r * rnd(0.3, 0.6), size1: 0, tint: col(), add: true, fadeIn: true });
        }
        break;
    }
  }

  // ─── Animation ────────────────────────────────────────────────────────

  update(dt: number): void {
    this.time += dt;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i];
      l.life -= dt;
      if (l.life <= 0) {
        l.layer.removeParticle(l.p);
        this.live[i] = this.live[this.live.length - 1];
        this.live.pop();
        continue;
      }
      const k = l.max > 1e8 ? 1 : l.life / l.max; // 1 → 0
      const p = l.p;
      if (l.orbit) {
        l.orbit.a += l.orbit.w * dt;
        p.x = l.orbit.c.x + Math.cos(l.orbit.a) * l.orbit.r;
        p.y = l.orbit.c.y + Math.sin(l.orbit.a) * l.orbit.r;
      } else {
        l.vy += l.grav * dt;
        l.vx *= l.drag ** (dt * 60);
        l.vy *= l.drag ** (dt * 60);
        p.x += l.vx * dt;
        p.y += l.vy * dt;
      }
      p.rotation += l.spin * dt;
      const s = l.s1 + (l.s0 - l.s1) * k;
      p.scaleX = s;
      p.scaleY = s;
      if (l.max < 1e8) p.alpha = l.a0 * (l.fadeIn ? Math.min(1, (1 - k) * 4, k * 2) : k);
      if (l.tint2 !== undefined) p.tint = lerpColor(l.tint2, l.tint, k);
    }
    for (let i = this.boltList.length - 1; i >= 0; i--) {
      const b = this.boltList[i];
      b.life -= dt;
      b.g.alpha = Math.max(0, b.life / b.max);
      if (b.life <= 0) {
        b.g.destroy();
        this.boltList.splice(i, 1);
      }
    }
  }

  get count(): number {
    return this.live.length;
  }
}
